import React from 'react';
import { MessageCircle, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * AssistantLauncher
 * Fixed circular FAB that toggles the AssistantPanel.
 * Purely controlled — all state lives in parent (Layout).
 *
 * Props:
 *   onClick  : () => void   — toggle handler
 *   isOpen   : boolean      — current panel open state
 */
export default function AssistantLauncher({ onClick, isOpen }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={isOpen ? 'Close NimbleNode Guide' : 'Open NimbleNode Guide'}
          aria-expanded={isOpen}
          aria-controls="nimblenode-assistant-panel"
          className={cn(
            // Positioning — z-[9998] so it sits below the panel (9999) but above sidebar (z-50)
            'fixed bottom-6 right-6 z-[9998]',
            // Mobile: clear native browser bottom bar + any mobile nav
            'lg:bottom-6 lg:right-6',
            'bottom-20 right-4',
            // Size
            'w-14 h-14 rounded-full',
            // Colors
            'bg-[#06B6D4] hover:bg-[#0891B2]',
            // Shadow with cyan glow
            'shadow-lg shadow-[#06B6D4]/30',
            // Icon transition
            'flex items-center justify-center',
            'transition-all duration-150 ease-in-out',
            // Focus ring
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A]',
            // Active scale
            'active:scale-95'
          )}
        >
          <span
            className={cn(
              'transition-all duration-150',
              isOpen ? 'scale-110 rotate-90' : 'scale-100 rotate-0'
            )}
          >
            {isOpen ? (
              <X className="w-6 h-6 text-white" aria-hidden="true" />
            ) : (
              <MessageCircle className="w-6 h-6 text-white" aria-hidden="true" />
            )}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="left"
        className="bg-[#1E293B] text-white border-[#334155] text-sm"
      >
        NimbleNode Guide
      </TooltipContent>
    </Tooltip>
  );
}
